import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, Tab, Tabs, Alert, Skeleton, Chip } from '@mui/material';
import axios from 'axios';

import MarketStatusBar from '../components/dashboard/MarketStatusBar';
import PortfolioHero from '../components/dashboard/PortfolioHero';
import NewsSection from '../components/dashboard/NewsSection';
import QuickStatsBar from '../components/dashboard/QuickStatsBar';

const Home = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (data) {
      const signals = data.signals || [];
      const ps = data.portfolioSummary;
      if (signals.length === 0 && (!ps || ps.totalValue === 0)) {
        navigate('/watchlist', { state: { showOnboarding: true } });
      }
    }
  }, [data, navigate]);

  const fetchDashboard = () => {
    setLoading(true);
    setError('');
    axios.get('/home')
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load dashboard'))
      .finally(() => setLoading(false));
  };

  const handleStockClick = (symbol) => {
    navigate(`/stock/${symbol}`);
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={40} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={<Typography variant="body2" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={fetchDashboard}>Retry</Typography>}>
        {error}
      </Alert>
    );
  }

  const {
    portfolioSummary: ps,
    marketIndices,
    marketOpen,
    marketRegime,
    sparkline,
    quickStats,
    topMovers = [],
    topLosers = [],
    signals = [],
    watchlistSignals = [],
    portfolioSymbols = [],
    news = [],
  } = data || {};

  const portfolioSet = new Set(portfolioSymbols);

  // Derive BUY/SELL/HOLD lists from watchlistSignals
  const buySignals = watchlistSignals.filter(s => s.signal === 'BUY');
  const sellSignals = watchlistSignals.filter(s => s.signal === 'SELL');
  const holdSignals = watchlistSignals.filter(s => !s.signal || s.signal === 'HOLD');

  const tabData = [
    { label: 'Buy', count: buySignals.length },
    { label: 'Sell', count: sellSignals.length },
    { label: 'Hold', count: holdSignals.length },
    { label: 'Movers', count: topMovers.length },
    { label: 'Losers', count: topLosers.length },
    { label: 'News', count: news.length },
  ];

  const renderSignalList = (items) => {
    if (items.length === 0) {
      return (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No signals</Typography>
        </Box>
      );
    }
    return (
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {items.map((s, i) => {
          const inPortfolio = portfolioSet.has(s.symbol);
          const isBuy = s.signal?.toUpperCase() === 'BUY';
          const isSell = s.signal?.toUpperCase() === 'SELL';
          const accentColor = isBuy ? 'success.main' : isSell ? 'error.main' : 'text.secondary';
          const bgColor = isBuy ? 'rgba(63,185,80,0.04)' : isSell ? 'rgba(248,81,73,0.04)' : 'transparent';

          return (
            <Box
              key={`${s.symbol}-${i}`}
              onClick={() => handleStockClick(s.symbol)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.2,
                borderRadius: 1.5,
                backgroundColor: bgColor,
                borderLeft: 3,
                borderLeftColor: accentColor,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
            >
              <Box sx={{ minWidth: 55 }}>
                <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  {s.symbol}
                </Typography>
                {inPortfolio && (
                  <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, fontSize: 10 }}>
                    IN PORTFOLIO
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                {s.company_name || s.symbol}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }} noWrap>
                {s.sector || '-'}
              </Typography>
              <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {s.current_price ? `$${parseFloat(s.current_price).toFixed(2)}` : '-'}
                </Typography>
                <Typography variant="caption" color={parseFloat(s.day_change_percent) >= 0 ? 'success.main' : 'error.main'}>
                  {s.day_change_percent ? `${parseFloat(s.day_change_percent).toFixed(1)}%` : ''}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 40, textAlign: 'right' }}>
                {s.confidence ? `${Math.round(s.confidence * 100)}%` : '-'}
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: return renderSignalList(buySignals);
      case 1: return renderSignalList(sellSignals);
      case 2: return renderSignalList(holdSignals);
      case 3:
        return topMovers.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No movers today</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {topMovers.map((stock, i) => {
              const inPortfolio = portfolioSet.has(stock.symbol);
              return (
                <Box
                  key={stock.symbol}
                  onClick={() => handleStockClick(stock.symbol)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, borderRadius: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Box sx={{ minWidth: 55 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{stock.symbol}</Typography>
                    {inPortfolio && <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, fontSize: 10 }}>IN PORTFOLIO</Typography>}
                  </Box>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{stock.company_name || stock.symbol}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }} noWrap>{stock.sector || '-'}</Typography>
                  <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>${parseFloat(stock.current_price || 0).toFixed(2)}</Typography>
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>+{parseFloat(stock.day_change_percent || 0).toFixed(1)}%</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      case 4:
        return topLosers.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No losers today</Typography>
          </Box>
        ) : (
          <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {topLosers.map((stock, i) => {
              const inPortfolio = portfolioSet.has(stock.symbol);
              return (
                <Box
                  key={stock.symbol}
                  onClick={() => handleStockClick(stock.symbol)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.2, borderRadius: 1.5, cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <Box sx={{ minWidth: 55 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{stock.symbol}</Typography>
                    {inPortfolio && <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, fontSize: 10 }}>IN PORTFOLIO</Typography>}
                  </Box>
                  <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>{stock.company_name || stock.symbol}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }} noWrap>{stock.sector || '-'}</Typography>
                  <Box sx={{ textAlign: 'right', minWidth: 70 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>${parseFloat(stock.current_price || 0).toFixed(2)}</Typography>
                    <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>{parseFloat(stock.day_change_percent || 0).toFixed(1)}%</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        );
      case 5:
        return <NewsSection articles={news} onSymbolClick={handleStockClick} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Market Status Bar */}
      <section aria-label="Market status">
        <MarketStatusBar
          indices={marketIndices || null}
          marketOpen={marketOpen ?? null}
          marketRegime={marketRegime || null}
        />
      </section>

      {/* Portfolio Hero */}
      {ps && (
        <section aria-label="Portfolio summary">
          <PortfolioHero
            totalValue={ps.totalValue}
            totalInvested={ps.totalInvested}
            todayPnL={ps.todayPnL || ps.totalGainLoss || 0}
            todayPnLPercent={ps.todayPnLPercent || ps.totalGainLossPercent || 0}
            positionsCount={ps.positionsCount}
            sparklineData={sparkline || null}
          />
        </section>
      )}

      {/* Tabbed Content Card */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 48, textTransform: 'none', fontWeight: 600, fontSize: 14 },
          }}
        >
          {tabData.map((tab, idx) => (
            <Tab
              key={tab.label}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {tab.label}
                  <Chip label={tab.count} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                </Box>
              }
            />
          ))}
        </Tabs>
        <Box sx={{ maxHeight: 420, overflowY: 'auto' }}>
          {renderTabContent()}
        </Box>
      </Card>

      {/* Quick Stats Bar */}
      <QuickStatsBar stats={quickStats || null} />
    </Box>
  );
};

export default Home;
